import { Seg } from '../../components/ui';
import { S as liveS } from '../../engine/state';
import { journal } from '../../engine/actions';
import { RULES } from '../../engine/version';
import { setLiveVisible } from '../leaderboard/live';
import { useEffect, useState } from 'react';
import { MedalCollection } from '../medals/OrdenUI';
import { syncMedals } from '../medals/store';
import { useAuth } from '../auth/AuthProvider';
import { getProfile, setUsername, myRuns, deleteAccount, type Profile as P, type MyRun } from './api';
import { supabase } from '../../lib/supabase';
import { status, disconnect } from '../multiplayer/sync';
import { track, setTelemetryUser } from '../../lib/telemetry';
import { consentDate } from '../auth/consent';
import { Link } from 'react-router-dom';

/** Profil-Seite für angemeldete Nutzer: Nutzername, eigene Partien, Abmelden. */
export function Profile() {
  const { user, signOut } = useAuth();
  const [p, setP] = useState<P | null>(null);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runs, setRuns] = useState<MyRun[]>([]);
  const [cd, setCd] = useState<string | null>(null);
  useEffect(() => { if (user?.id) consentDate(user.id).then(setCd); }, [user?.id]);
  useEffect(() => { getProfile().then((x) => { setP(x); setName(x?.username ?? ''); }); myRuns().then(setRuns); void syncMedals(); }, [user?.id]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true);
    const err = await setUsername(name); setBusy(false);
    setMsg(err ?? 'Gespeichert.'); if (!err) setP((x) => x ? { ...x, username: name.trim() } : x);
  };

  return (
    <section className="screen">
      <div><span className="label">Profil</span><h2>{p?.username ? p.username : 'Angemeldet'}</h2></div>
      <p className="sub">{user?.email}{cd ? <> · <Link to="/datenschutz">Datenschutzerklärung</Link> zugestimmt am {new Date(cd).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}</> : null}</p>
      <form className="form" onSubmit={save}>
        <label className="label" htmlFor="username">Nutzername in der Bestenliste</label>
        <input id="username" type="text" maxLength={24} value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. kanzlerin_2029" />
        <div className="actions"><button type="submit" className="yes" disabled={busy || !name.trim()}>Speichern</button>{msg && <span className="sub">{msg}</span>}</div>
      </form>
      <LiveToggle visible={(p as any)?.live_visible !== false} />
      <div><span className="label">Eure Partien</span></div>
      {!runs.length ? <p className="sub">Noch keine beendeten Partien.</p> : (
        <div className="lb">{runs.map((r) => <div className="row" key={r.id}><span className="rank">{r.verified ? '✓' : '·'}</span><div className="mi"><b>{r.party_name}</b><span className="sub">{new Date(r.created_at).toLocaleDateString('de-DE')} · {r.legislatures} {r.legislatures === 1 ? 'Legislatur' : 'Legislaturen'} · {r.laws} Gesetze · {r.difficulty}{r.verified ? ' · geprüft' : ''}</span></div><b className="num">{r.score.toLocaleString('de-DE')}</b></div>)}</div>)}
      <div><span className="label">Eure Orden</span></div>
      <MedalCollection />
      <div className="actions"><button type="button" onClick={signOut}>Abmelden</button></div>
      {debugOn() && <DebugExport />}
      <DeleteAccount />
    </section>
  );
}

/** Konto endgültig löschen. Entfernt alle Daten in Supabase und auf Wunsch auch den Spielstand auf diesem Gerät. */
function DeleteAccount() {
  const [open, setOpen] = useState(false); const [word, setWord] = useState(''); const [local, setLocal] = useState(true);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true); setErr(null);
    if (status()) await disconnect(true);
    const e = await deleteAccount();
    if (e) { setErr(e); setBusy(false); return; }
    track('User.deleted', { localDataRemoved: local });
    setTelemetryUser(null);
    try { await supabase?.auth.signOut({ scope: 'local' }); } catch { /* Konto ist bereits weg */ }
    if (local) { try { for (const k of Object.keys(localStorage)) if (k.startsWith('kabinett')) localStorage.removeItem(k); } catch { /* egal */ } }
    window.location.assign('/?konto=geloescht');
  };
  return (
    <div className="danger">
      <span className="label">Konto löschen</span>
      {!open ? <>
        <p className="sub">Löscht dein Konto und alle zugehörigen Daten unwiderruflich: E-Mail-Adresse, Nutzername, Cloud-Spielstände, Einträge in der Bestenliste, deine Zustimmungen und alle Mehrspieler-Partien, an denen du beteiligt warst.</p>
        <div className="actions"><button type="button" className="warnbtn" onClick={() => setOpen(true)}>Konto löschen …</button></div>
      </> : <>
        <p>Das lässt sich nicht rückgängig machen. Laufende Mehrspieler-Partien mit dir enden auch für deine Mitspieler.</p>
        <label className="consent" htmlFor="dl-local"><input id="dl-local" type="checkbox" checked={local} onChange={(e) => setLocal(e.target.checked)} /><span>Auch den Spielstand auf diesem Gerät löschen</span></label>
        <label className="label" htmlFor="dl-word">Zur Bestätigung LÖSCHEN eintippen</label>
        <input id="dl-word" type="text" value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" style={{ maxWidth: 220 }} />
        <div className="actions">
          <button type="button" className="warnbtn" disabled={busy || word.trim().toUpperCase() !== 'LÖSCHEN'} onClick={go}>{busy ? 'Wird gelöscht …' : 'Konto endgültig löschen'}</button>
          <button type="button" onClick={() => { setOpen(false); setWord(''); setErr(null); }}>Abbrechen</button>
        </div>
        {err && <span className="sub down">{err}</span>}
      </>}
    </div>
  );
}

/** Live-Liste der Bestenliste: laufende Regierung zeigen oder nicht. */
function LiveToggle({ visible }: { visible: boolean }) {
  const [on, setOn] = useState(visible); const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => setOn(visible), [visible]);
  return (
    <div className="param"><span className="pl">Laufende Regierung in der Live-Liste zeigen</span>
      <Seg value={on ? 1 : 0} options={[[1, 'Ja'], [0, 'Nein']]} onChange={async (v) => { const want = v === 1; setOn(want); const e = await setLiveVisible(want); setMsg(e); if (e) setOn(!want); }} />
      <span className="sub">{msg ?? 'Zeigt Nutzername, Partei, Legislatur, Umfragewert und Schwierigkeit deiner laufenden Partie unter „Bestenliste → Laufende Regierungen“.'}</span>
    </div>
  );
}

/** Nur für die Fehlersuche: sichtbar mit ?debug in der Adresse (z. B. /profil?debug). */
const debugOn = (): boolean => { try { return new URLSearchParams(window.location.search).has('debug'); } catch { return false; } };

/** Spielstand mit Zugprotokoll als Datei, damit sich Fehler beim Nachspielen untersuchen lassen. */
function DebugExport() {
  const save = () => {
    const data = { exportedAt: new Date().toISOString(), build: typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev', rules: RULES, state: liveS, journal: journal.slice() };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = `kabinett-spielstand-${(liveS?.name || 'partie').replace(/[^\wäöüÄÖÜß-]+/g, '_')}.json`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="param"><span className="pl">Spielstand für die Fehlersuche</span>
      <button type="button" className="small" onClick={save} disabled={!liveS}>Spielstand herunterladen</button>
      <span className="sub">Lädt deinen aktuellen Spielstand mit allen Zügen als Datei herunter, zum Beispiel wenn die Prüfung in der Bestenliste nicht klappt.</span>
    </div>
  );
}
